import Loader from "components/global/Loader";
import NoNotifications from "./NoNotifications";
import Spacer from "components/global/Spacer";
import styled from "styled-components";
import { useClickOutside } from "webrix/hooks";
import { getTimeAgo } from "utils/utils";
import { useState } from "react";
import { Tab } from "views/postPlanner/styles/PostStyles";
import ImgWrapper from "components/global/ImgWrapper";
import { Link } from "react-router-dom";
import userIcon from "assets/icons/userIconWhite.svg";
import { socialIcons } from "views/socialMedia/ManageChannels";
import PlatformPhoto from "components/global/PlatformPhoto";

const Wrapper = styled.div`
  width: 360px;
  box-shadow: 0px 25px 35px 0px rgba(0, 0, 0, 0.03),
    0px 0px 5px 0px rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  position: absolute;
  top: 3.6rem;
  right: 0;
  border-radius: 8px;

  .header {
    height: 6rem;
    padding: 0 12px;
  }

  .content {
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    max-height: calc(100vh - 12rem);
    min-height: 24.3rem;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .tab {
    padding-inline: 12px;
    z-index: 10;

    .item {
      width: 50%;
    }
  }

  @media screen and (max-width: 768px) {
    position: fixed;
    top: 6.4rem;
    right: 2.4rem;
    width: calc(100vw - 4.8rem);
    margin: auto;
    z-index: 20;
  }
`;

const NotificationItem = styled.div`
  padding: 24px 12px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;

  &:nth-child(1) {
    border-top: none;
  }

  .message {
    overflow-wrap: anywhere;
  }

  .draftLink {
    display: flex;
    padding: 8px 12px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border: 1px solid var(--grey_10);
    border-radius: 8px;
    width: max-content;
  }

  .blueRound {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #fff;
    border: 1px solid var(--secondary);
  }
`;

const ResourceLink = styled.div`
  padding: 6px;
  border-radius: 8px;
  gap: 6px;

  .media {
    width: 42px;
    height: 42px;
    border-radius: 8px;
  }

  .text {
    min-width: calc(100% - 16px - 72px - 20px - 18px);
  }

  .dateTime {
    width: 80px;
  }

  .platformData {
    width: 20px;

    .platformImg {
      height: 10px;
      width: 10px;
      left: 12px;
      bottom: 0;
    }
  }
`;

const notificationTypeMap = {
  general: 1,
  mentions: 2,
};

const NotificationList = ({
  notifications,
  setShowNotifications,
  loading,
  isFetchingNextPage,
  lastListElementRef,
  brands,
}) => {
  const [activeTab, setActiveTab] = useState("general");

  const handleOnMouseDownCapture = useClickOutside(() => {
    setShowNotifications(false);
  });

  return (
    <Wrapper onMouseDownCapture={handleOnMouseDownCapture}>
      <div className="header justifySpaceBetween flexRow alignCenter">
        <p className="l2 bold">Notifications</p>
      </div>
      <div className="content">
        <Tab className="flexRow stickyTop tab">
          <button
            className={`flexRow alignCenter justifyCenter item${
              activeTab === "general" ? " active" : ""
            }`}
            onClick={() => setActiveTab("general")}
          >
            <span className="l2">General</span>
          </button>
          <button
            className={`flexRow alignCenter justifyCenter item${
              activeTab === "mentions" ? " active" : ""
            }`}
            onClick={() => setActiveTab("mentions")}
          >
            <span className="l2">Mentions</span>
          </button>
        </Tab>
        {notifications
          ?.filter((item) => item.data.type === notificationTypeMap[activeTab])
          ?.map((notification, index) => {
            const {
              data: { message, resource_data, resource_type, meta },
              created_at,
            } = notification;
            const { actor, draft_id } = meta;
            const {
              id,
              media,
              content,
              updated_at: resource_updated_at,
              created_at: resource_created_at,
              brand,
              status,
              platform,
              media_thumb,
            } = resource_data;

            const brandDetails = brands?.find((item) => item.id === brand?.id);
            const platformData = brandDetails?.socials?.socials?.find(
              (channel) => {
                return channel.platform === platform;
              }
            );

            return (
              <NotificationItem
                key={index}
                ref={
                  notifications.length === index + 1 ? lastListElementRef : null
                }
                className="flexRow"
              >
                <div className="flexRow">
                  <div className="blueRound" />
                  <ImgWrapper as="button" bg="var(--primary)" size={3.2}>
                    {actor?.avatar_thumb ? (
                      <img src={`${actor.avatar_thumb}`} alt="avatar" />
                    ) : (
                      <span className="initials textCenter textUppercase textSmall colorWhite">{`${
                        actor?.first_name?.charAt(0) ?? ""
                      }${actor?.last_name?.charAt(0) ?? ""}`}</span>
                    )}
                  </ImgWrapper>
                </div>
                <div>
                  <p
                    className="l3 message"
                    dangerouslySetInnerHTML={{ __html: message }}
                  ></p>
                  {resource_data && content && (
                    <>
                      <Spacer y={0.6} />
                      <ResourceLink className="fullWidth flexRow alignCenter l4">
                        {!!media?.length || media_thumb?.length ? (
                          <div className="flexRow alignCenter justifyCenter media">
                            <img
                              src={media_thumb?.[0] || media?.[0]}
                              alt="media"
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "4px",
                              }}
                            />
                          </div>
                        ) : null}
                        <div>
                          <div className="colorBodyText textLeft text">
                            {content.slice(0, 25)}
                            {content.length > 25 ? "..." : ""}
                          </div>
                          <div className="flexRow alignCenter">
                            {platformData && (
                              <img
                                src={socialIcons.circle[platform]}
                                alt=""
                                width="16px"
                                height="16px"
                              />
                            )}
                            <div className="colorBodyText textSmall dateTime">
                              {new Date(
                                resource_updated_at ?? resource_created_at
                              ).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                // year: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                              })}
                            </div>
                          </div>
                        </div>
                      </ResourceLink>
                    </>
                  )}
                  {draft_id && (
                    <>
                      <Spacer y={0.6} />
                      <Link
                        to={`/dashboard/posts/list-view?tab=draft&draftId=${draft_id}&post_action=edit_draft`}
                        className="draftLink"
                        onClick={() => setShowNotifications(false)}
                      >
                        View post
                      </Link>
                    </>
                  )}
                  <Spacer y={0.6} />

                  <Spacer y={0.6} />
                  {notification.created_at && (
                    <p className="textSmall colorBodyText">
                      {getTimeAgo(created_at)}
                    </p>
                  )}
                  {(status === "failed" || resource_type === "draft") && (
                    <>
                      <Spacer y={1.6} />
                      <Link
                        to={`/dashboard/${resource_type}s/${id}${
                          status === "failed" ? "?isFailed=true" : ""
                        }`}
                        className="draftLink"
                        onClick={() => setShowNotifications(false)}
                      >
                        View {resource_type}
                      </Link>
                    </>
                  )}
                </div>
              </NotificationItem>
            );
          })}
        {!notifications?.length && !loading && <NoNotifications />}
        {loading && (
          <>
            <Spacer y={2.4} />
            <Loader type="spinner" />
          </>
        )}
        {isFetchingNextPage && (
          <>
            <Spacer y={1.6} />
            <div style={{ textAlign: "center" }}>
              <Loader type="spinner" />
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default NotificationList;
